/**
 * @fileoverview Object flattening utilities for nested JSON structures.
 * @module helpers/flatten
 */

import flat from "flat";

/**
 * Flattens a nested JSON object into a single-level object with dot notation keys.
 *
 * Parses JSON string input and flattens nested objects. Numbers are not flattened.
 * Returns empty string for falsy values and original value if parsing fails.
 *
 * @param {string|*} value - JSON string to parse and flatten, or any value
 * @returns {Object|string} Flattened object, empty string, or original value
 *
 * @example
 * flatten('{"user": {"name": "John", "address": {"city": "NYC"}}}');
 * // Returns: { 'user.name': 'John', 'user.address.city': 'NYC' }
 *
 * flatten('123'); // Returns: '123' (numbers not flattened)
 * flatten(null);  // Returns: ''
 */
function flatten(value) {
  if (!value) {
    return "";
  }
  try {
    const output = JSON.parse(value);
    if (typeof output === "number") {
      throw new Error();
    }
    return flat(output);
  } catch (e) {
    return value;
  }
}

/**
 * Checks if a string appears to be valid JSON object format.
 *
 * Simple check that verifies the string starts with '{' and ends with '}'.
 * Does not validate actual JSON syntax.
 *
 * @param {string} data - The string to check
 * @returns {boolean} True if string looks like a JSON object
 *
 * @example
 * isFlatAble('{"key": "value"}'); // Returns: true
 * isFlatAble('[1, 2, 3]');        // Returns: false
 * isFlatAble('plain text');       // Returns: false
 */
function isFlatAble(data) {
  return data.startsWith("{") && data.endsWith("}");
}

export { flatten, isFlatAble };
